import {useTranslationContext} from 'platform/components';

import {useRef, useState} from 'react';

import {buildCategoryItems} from '../../../../hooks/useFilterCategorySelection';
import type {EagColumnOrGroup} from '../../../../types/EagColumnGroup';
import type {Filters} from '../../../../types/Filters';
import {filterCategoriesBySearch} from '../FilterContentArea/utils/filterCategoriesBySearch';
import {FilterAccordionSection} from './components/FilterAccordionSection/FilterAccordionSection';
import {FilterControlsRow} from './components/FilterControlsRow/FilterControlsRow';
import {getExpandedIdsForSearchChange} from './utils/getExpandedIdsForSearchChange';

type CategoryItem = ReturnType<typeof buildCategoryItems>[number];

/**
 * Default expanded: sections that have active filters; when none are active,
 * expand only the FIRST section so the panel reads as a compact list of
 * collapsible sections (matching the design) rather than a wall of fields.
 */
function computeDefaultExpandedIds(allCategories: CategoryItem[]): Set<string> {
  return new Set(
    allCategories.some((c) => c.activeCount > 0)
      ? allCategories.filter((c) => c.activeCount > 0).map((c) => c.id)
      : allCategories.slice(0, 1).map((c) => c.id)
  );
}

type FilterPanelProps = {
  columnConfig: EagColumnOrGroup[];
  filterState: Filters;
  onFilterChange: (key: string, value: unknown) => void;
};

/**
 * Main filter panel for the desktop narrow slide-in filter UI.
 *
 * Layout (Direction-B accordion design):
 * ```
 * ┌─────────────────────────────┐
 * │ [Search filters…]           │  ← searchTerm state
 * │ [✓ Show active only] [Expand all | Collapse] │
 * ├─────────────────────────────┤
 * │ ▶ General          ● 2  12  │  ← accordion section header
 * │   [filter widgets…]         │  ← expanded body
 * ├─────────────────────────────┤
 * │ ▶ Pricing              4   │
 * └─────────────────────────────┘
 * ```
 *
 * ## Behavior
 * - Sections with active filters start expanded; others start collapsed.
 * - "Show active only" hides sections with no active filters.
 * - Search filters which sections/fields are shown: a field match surfaces its
 *   section, a section-name match shows all of that section's fields. Sections
 *   the term surfaces expand automatically; clearing the term restores the
 *   expansion the user had before searching (toggles made while searching are
 *   discarded).
 * - "Expand all" / "Collapse" toggle all visible sections at once.
 * - Filters are applied live as values change (no batch apply).
 *
 * ## Why props, not store reads
 *
 * `filterState` and `onFilterChange` are stable callbacks injected from
 * `EagGridTable` via `Sidebar`. This keeps the panel decoupled from the
 * store and trivially testable without a provider.
 */
export function FilterPanel(props: FilterPanelProps) {
  const t = useTranslationContext();
  const [searchTerm, setSearchTerm] = useState('');
  const [showActiveOnly, setShowActiveOnly] = useState(false);

  // Build all category items (id, label, activeCount, childLabels)
  const allCategories = buildCategoryItems(props.columnConfig, props.filterState);

  // Apply search filter: a section matches if its label OR any child label contains the term.
  const searchFilteredCategories = filterCategoriesBySearch(allCategories, searchTerm);

  // Apply "show active only" filter
  const visibleCategories = showActiveOnly
    ? searchFilteredCategories.filter((cat) => cat.activeCount > 0)
    : searchFilteredCategories;

  const [expandedIds, setExpandedIds] = useState<Set<string>>(() =>
    computeDefaultExpandedIds(allCategories)
  );

  // Expansion captured when a search starts; restored when the term is cleared.
  const preSearchExpandedIdsRef = useRef<Set<string> | null>(null);

  const handleSearchChange = (value: string) => {
    const next = getExpandedIdsForSearchChange({
      previousTerm: searchTerm,
      nextTerm: value,
      expandedIds,
      snapshot: preSearchExpandedIdsRef.current,
      categories: allCategories,
    });
    preSearchExpandedIdsRef.current = next.snapshot;
    setExpandedIds(next.expandedIds);
    setSearchTerm(value);
  };

  const toggleSection = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const expandAll = () => {
    setExpandedIds(new Set(visibleCategories.map((c) => c.id)));
  };

  const collapseAll = () => {
    setExpandedIds(new Set());
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Search + controls */}
      <div
        className="flex flex-col gap-2 px-3 py-2 flex-shrink-0"
        style={{borderBottom: '1px solid #EBECF0'}}
      >
        <div className="relative">
          <svg
            className="absolute text-[#6B778C]"
            style={{left: '11px', top: '50%', transform: 'translateY(-50%)'}}
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
            <line
              x1="16.5"
              y1="16.5"
              x2="21"
              y2="21"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
          <input
            type="text"
            placeholder="Search filters…"
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            data-testid="filter-search-input"
            className="w-full rounded border border-[#C1C7D0] bg-white text-[#091E42] placeholder-[#6B778C] outline-none focus:border-[#4C9AFF] focus:ring-1 focus:ring-[#4C9AFF]"
            style={{height: '32px', paddingLeft: '34px', paddingRight: '10px', fontSize: '13px'}}
          />
        </div>
        <FilterControlsRow
          showActiveOnly={showActiveOnly}
          onShowActiveOnlyChange={setShowActiveOnly}
          onExpandAll={expandAll}
          onCollapseAll={collapseAll}
        />
      </div>

      {/* Accordion sections — scrollable body */}
      <div className="flex-1 overflow-y-auto">
        {visibleCategories.length === 0 ? (
          <div className="flex items-center justify-center py-12 text-sm text-[#6B778C] px-4 text-center">
            {showActiveOnly
              ? t('page.dataGrid.filter.noFiltersAvailable')
              : t('page.dataGrid.filter.noFiltersMatchSearch')}
          </div>
        ) : (
          <div className="flex flex-col px-3">
            {visibleCategories.map((category) => (
              <FilterAccordionSection
                key={category.id}
                category={category}
                isExpanded={expandedIds.has(category.id)}
                onToggle={toggleSection}
                columnConfig={props.columnConfig}
                filterState={props.filterState}
                onFilterChange={props.onFilterChange}
                searchTerm={searchTerm}
                showActiveOnly={showActiveOnly}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
